import type { IconDefinition } from '../../elements/icon.ts';
import { groupElement, iconElement, textElement, type LayoutElement } from '../../elements/layout_elements.ts';
import { formatNumber } from '@casa/util';
import type { TileFont } from '../value/value_tile.ts';

/**
 * The legacy `airquality` tile (`AirQualityTile.cs`, `AirQualityTileView.xaml`): three columns,
 * CO₂, PM₂₅ (written so in the legacy view) and VOC, each with its name, a trend arrow and the value in a colour by threshold.
 * The legacy view ignored the dashboard's `measurements` option, and so does this one.
 */

export interface AirQualityValues {
    co2: number | null;
    pm25: number | null;
    /** Total VOC in ppb (the AirGradient driver's `tvoc_ppb`). */
    tvocPpb: number | null;
    co2Trend: number | null;
    pm25Trend: number | null;
    tvocPpbTrend: number | null;
}

export type Trend = 'up' | 'down' | 'flat';

export interface AirQualityColumn {
    name: string;
    text: string;
    color: string;
    trend: Trend;
}

export interface TrendIcons {
    up?: IconDefinition | undefined;
    down?: IconDefinition | undefined;
    flat?: IconDefinition | undefined;
}

const GREEN = '#008000';
const YELLOW = '#ffff00';
const ORANGE = '#ffa500';
const RED = '#ff0000';
const GRAY = '#808080';
export const WHITE = '#ffffff';

const TREND_COLORS: Readonly<Record<Trend, string>> = { up: ORANGE, down: GREEN, flat: GRAY };

type Thresholds = readonly (readonly [limit: number, color: string])[];

const CO2_THRESHOLDS: Thresholds = [[1000, GREEN], [1200, YELLOW], [1600, ORANGE]];
const PM25_THRESHOLDS: Thresholds = [[5, GREEN], [10, YELLOW], [20, ORANGE]];
const VOC_THRESHOLDS: Thresholds = [[2500, GREEN], [4000, YELLOW], [6000, ORANGE], [9000, RED]];

/** The colour of the first threshold the value is below; red above all of them and without a value, as in the legacy tile. */
function thresholdColor(value: number | null, thresholds: Thresholds) : string {
    if (value === null) {
        return RED;
    }

    return thresholds.find(([limit]) => value < limit)?.[1] ?? RED;
}

function trend(value: number | null) : Trend {
    if (value === null || value === 0) {
        return 'flat';
    }

    return value > 0 ? 'up' : 'down';
}

/** .NET formats a missing value as an empty string. */
function format(value: number | null, numberFormat: string, locale: string | undefined) : string {
    return value === null ? '' : formatNumber(value, numberFormat, locale);
}

export function airQualityColumns(values: AirQualityValues, locale?: string) : AirQualityColumn[] {
    const { co2, pm25, tvocPpb } = values;

    return [
        { name: 'CO₂', text: format(co2, '#,##0', locale), color: thresholdColor(co2, CO2_THRESHOLDS), trend: trend(values.co2Trend) },
        {
            name: 'PM₂₅',
            text: format(pm25, pm25 !== null && pm25 > 9.99 ? '0' : '0.0', locale),
            color: thresholdColor(pm25, PM25_THRESHOLDS),
            trend: trend(values.pm25Trend),
        },
        {
            name: 'VOC',
            text: format(tvocPpb === null ? null : tvocPpb / 1000, tvocPpb !== null && tvocPpb > 9999 ? '0.0' : '0.00', locale),
            color: thresholdColor(tvocPpb, VOC_THRESHOLDS),
            trend: trend(values.tvocPpbTrend),
        },
    ];
}

/** A `PercentLayout` child: position and size in percent of the column. */
const percent = (x: number, y: number, w: number, h: number) => ({
    left: `parent.width*${x / 100}`,
    top: `parent.height*${y / 100}`,
    w: `parent.width*${w / 100}`,
    h: `parent.height*${h / 100}`,
});

/** The grid: padding 10, three equal columns with 6 between them. */
const PADDING = 10;
const SPACING = 6;

export function airQualityTileElements(columns: readonly AirQualityColumn[], icons: TrendIcons, font: TileFont, textColor = WHITE) : LayoutElement[] {
    const columnWidth = `((parent.width-${2 * PADDING + 2 * SPACING})/3)`;

    return columns.map((column, index) =>
        groupElement({ left: `${PADDING}+${index}*(${columnWidth}+${SPACING})`, top: `${PADDING}`, w: columnWidth, h: `parent.height-${2 * PADDING}` }, [
            textElement(percent(0, 0, 100, 30), column.name, textColor, { family: font.family, metrics: font.metrics }),
            // 80 % of the column, centred: a number as wide as its column ran into the next one's in a
            // wide, short tile ("4353.40.20", the demo phone's, 2026-11-03).
            textElement(percent(10, 55, 80, 40), column.text, column.color, { family: font.family, metrics: font.metrics, fill: true }),
            iconElement(percent(0, 35, 100, 10), icons[column.trend], TREND_COLORS[column.trend], { fill: true }),
        ]),
    );
}
